import React, { useEffect, useState, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Col, Container, Row, Card, CardBody, Badge, Table, Spinner, Button } from "reactstrap";
import {
    FiTag, FiTruck, FiDollarSign, FiTrash2, FiPlus,
    FiSettings, FiBox, FiInfo, FiImage, FiActivity, FiLayers, FiList,
    FiMove, FiChevronUp, FiChevronDown, FiEdit2
} from "react-icons/fi";
import { IoCloseSharp } from "react-icons/io5";
import PageName from "../../components/react_helmet/PageName";
import { getProductGroup, removeVariantFromGroup, reorderVariants, deleteProductGroup } from "../../api/products.api";
import CreateProductGroupModal from "../../components/modals/CreateProductGroupModal";
import toast from "react-hot-toast";
import SkeletonLoader from "../../components/loaders/SkeletonLoader";
import moment from "moment";

const ProductGroupDetails = () => {
    const history = useHistory();
    const { id } = useParams();

    const [loading, setLoading] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [groupData, setGroupData] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const [attributeKeys, setAttributeKeys] = useState([]);

    // Synchronize attribute keys and variant attribute values when groupData loads
    useEffect(() => {
        if (groupData) {
            const keysSet = new Set();
            if (Array.isArray(groupData.variantAttributes)) {
                groupData.variantAttributes.forEach((k) => {
                    if (k && typeof k === "string") keysSet.add(k.trim());
                });
            }
            if (Array.isArray(groupData.variants)) {
                groupData.variants.forEach((v) => {
                    const attrs = v.variantAttributes || v.attributes;
                    if (attrs && typeof attrs === "object") {
                        Object.keys(attrs).forEach((k) => {
                            if (k && typeof k === "string") keysSet.add(k.trim());
                        });
                    }
                });
            }
            setAttributeKeys(Array.from(keysSet));
        }
    }, [groupData]);

    const rawGroupType = (groupData?.groupType || groupData?.type || groupData?.group_type || "").toLowerCase().trim();
    const isRelated = rawGroupType === "related" || rawGroupType === "related_products" || rawGroupType === "related_group";
    const isBundle = rawGroupType === "bundle" || rawGroupType === "bundle_group";
    const isVariant = rawGroupType === "variant" || rawGroupType === "variant_group" || rawGroupType === "variants" || (!isRelated && !isBundle);

    const groupTypeLabel = isRelated
        ? "Related Group"
        : isBundle
        ? "Bundle Group"
        : "Variant Group";

    const itemTypeLabel = isRelated
        ? "Related Product"
        : isBundle
        ? "Bundle Item"
        : "Variant";

    const itemsPluralLabel = isRelated
        ? "Related Products"
        : isBundle
        ? "Bundle Items"
        : "Variants";

    const handleDeleteGroup = () => {
        if (!window.confirm(`Are you sure you want to delete this ${groupTypeLabel.toLowerCase()} "${groupData?.name || ''}"? This action cannot be undone.`)) return;
        setLoading(true);
        deleteProductGroup(id, (res) => {
            if (res.status === 200 || res?.success) {
                toast.success(`${groupTypeLabel} deleted successfully!`);
                history.push("/admin/product-groups");
            } else {
                toast.error(res.message || `Failed to delete ${groupTypeLabel.toLowerCase()}.`);
                setLoading(false);
            }
        });
    };

    const handleRemoveVariant = (productId) => {
        if (!window.confirm(`Are you sure you want to remove this ${itemTypeLabel.toLowerCase()} from the ${groupTypeLabel.toLowerCase()}?`)) return;
        setLoading(true);
        removeVariantFromGroup({ groupId: id, productId }, (res) => {
            if (res.status === 200 || res.status === 201 || res?.success) {
                toast.success(`${itemTypeLabel} removed successfully!`);
                fetchData();
            } else {
                toast.error(res.message || `Failed to remove ${itemTypeLabel.toLowerCase()}.`);
                setLoading(false);
            }
        });
    };

    const handleReorder = (newVariants) => {
        setGroupData(prev => ({
            ...prev,
            variants: newVariants
        }));

        const orderPayload = newVariants.map((v, idx) => ({
            productId: v.productId || v._id || v.id,
            sortOrder: idx
        }));

        setReordering(true);
        reorderVariants({ groupId: id, order: orderPayload }, (res) => {
            setReordering(false);
            if (res.status === 200 || res.status === 201 || res?.success) {
                toast.success(`${itemsPluralLabel} reordered successfully!`);
            } else {
                toast.error(res.message || `Failed to reorder ${itemsPluralLabel.toLowerCase()}.`);
                fetchData();
            }
        });
    };

    const moveVariant = (fromIndex, toIndex) => {
        if (!groupData?.variants) return;
        if (toIndex < 0 || toIndex >= groupData.variants.length) return;

        const updated = [...groupData.variants];
        const [movedItem] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, movedItem);

        handleReorder(updated);
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === undefined) return;
        if (draggedIndex === targetIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        moveVariant(draggedIndex, targetIndex);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id]);

    const fetchData = () => {
        setLoading(true);
        getProductGroup(id, (res) => {
            if (res.status === 200 || res.status === 201 || res?.success) {
                const data = res?.data?.data || res?.data;
                if (res?.data?.variants) {
                    data.variants = res.data.variants;
                }
                setGroupData(data);
            } else {
                toast.error(res?.message || "Failed to fetch group details.");
            }
            setLoading(false);
        });
    };

    if (loading) {
        return (
            <div className="settings-container p-4">
                <SkeletonLoader count={3} />
            </div>
        );
    }

    if (!groupData) {
        return (
            <div className="settings-container p-4 text-center">
                <h4 className="text-muted mt-5">Group not found</h4>
                <Button color="primary" onClick={() => history.goBack()} className="mt-3">
                    Go Back
                </Button>
            </div>
        );
    }

    return (
        <div className="settings-container pb-5">
            <CreateProductGroupModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(!editModalOpen)}
                refreshData={fetchData}
                editData={editData}
                setEditData={setEditData}
            />
            <PageName title={`${groupData?.name || groupTypeLabel} - Details`} />

            <div className="px-4">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="bg-primary-soft p-3 rounded-circle shadow-sm">
                            <FiBox size={24} className="text-primary" />
                        </div>
                        <div>
                            <h4 className="fw-bold text-dark mb-0">{groupData?.name || `Unnamed ${groupTypeLabel}`}</h4>
                            <p className="text-muted small mb-0 mt-1 d-flex align-items-center gap-2">
                                <Badge color={isVariant ? "primary" : isRelated ? "info" : "success"} pill>
                                    {groupData?.groupType?.toUpperCase()}
                                </Badge>
                                <span>Slug: {groupData?.slug || "N/A"}</span>
                            </p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Button 
                            color="primary" 
                            className="shadow-sm d-flex align-items-center gap-2" 
                            onClick={() => {
                                setEditData(groupData);
                                setEditModalOpen(true);
                            }}
                        >
                            <FiEdit2 size={16} /> Edit {groupTypeLabel}
                        </Button>
                        <Button color="danger" outline className="shadow-sm d-flex align-items-center gap-2" onClick={handleDeleteGroup}>
                            <FiTrash2 size={16} /> Delete {groupTypeLabel}
                        </Button>
                        <Button color="light" className="shadow-sm d-flex align-items-center gap-2" onClick={() => history.goBack()}>
                            <IoCloseSharp size={20} /> Close
                        </Button>
                    </div>
                </div>

                <Row className="g-4">
                    <Col md={8}>
                        {/* Basic Info */}
                        <Card className="border-0 shadow-sm mb-4">
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                        <FiInfo size={16} />
                                    </div>
                                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider">Basic Information</h6>
                                </div>
                                <Row className="g-3">
                                    {!isRelated && (
                                        <>
                                            <Col md={12}>
                                                <label className="text-muted small mb-1">Short Description</label>
                                                <div className="fw-bold text-dark">{groupData?.description || "N/A"}</div>
                                            </Col>
                                            <Col md={12}>
                                                <label className="text-muted small mb-1">Long Description</label>
                                                <div className="text-dark bg-light p-3 rounded" style={{ fontSize: '14px', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: groupData?.long_description || "N/A" }} />
                                            </Col>
                                        </>
                                    )}
                                    <Col md={4}>
                                        <label className="text-muted small mb-1">Status</label>
                                        <div><Badge color={groupData?.status === "publish" ? "success" : "secondary"}>{groupData?.status?.toUpperCase() || "N/A"}</Badge></div>
                                    </Col>
                                    <Col md={4}>
                                        <label className="text-muted small mb-1">Created At</label>
                                        <div className="fw-bold text-dark">{groupData?.date_created ? moment(groupData?.date_created).format("MMM DD, YYYY") : "N/A"}</div>
                                    </Col>
                                    <Col md={4}>
                                        <label className="text-muted small mb-1">Updated At</label>
                                        <div className="fw-bold text-dark">{groupData?.updated_at ? moment(groupData?.updated_at).format("MMM DD, YYYY") : "N/A"}</div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        {/* Variants / Items Table with Drag & Drop Reordering */}
                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                            <FiList size={16} />
                                        </div>
                                        <div>
                                            <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider">
                                                {itemsPluralLabel} ({groupData?.variants?.length || 0})
                                            </h6>
                                            <small className="text-muted">Drag rows or use arrows to reorder.</small>
                                        </div>
                                        {reordering && <Spinner size="sm" color="primary" className="ms-2" />}
                                    </div>
                                </div>
                                {groupData?.variants && groupData.variants.length > 0 ? (
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th style={{ width: "60px" }}>#</th>
                                                    <th style={{ minWidth: "180px" }}>Name / SKU</th>
                                                    <th style={{ width: "85px" }}>Price</th>
                                                    <th style={{ width: "70px" }}>Stock</th>
                                                    {isVariant && attributeKeys.map((attrKey, kIdx) => (
                                                        <th key={kIdx} style={{ minWidth: "110px" }}>
                                                            <div className="d-flex align-items-center gap-1 text-primary fw-bold text-capitalize">
                                                                <FiTag size={13} />
                                                                <span>{attrKey}</span>
                                                            </div>
                                                        </th>
                                                    ))}
                                                    <th style={{ width: "85px" }}>Condition</th>
                                                    <th style={{ width: "85px" }}>Status</th>
                                                    <th style={{ width: "110px" }} className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {groupData.variants.map((variant, idx) => {
                                                    const pId = variant._id || variant.id || variant.productId;
                                                    const isDragging = draggedIndex === idx;
                                                    const isDragOver = dragOverIndex === idx;
                                                    return (
                                                        <tr 
                                                            key={pId || idx}
                                                            draggable={!reordering}
                                                            onDragStart={(e) => handleDragStart(e, idx)}
                                                            onDragOver={(e) => handleDragOver(e, idx)}
                                                            onDrop={(e) => handleDrop(e, idx)}
                                                            onDragEnd={handleDragEnd}
                                                            style={{
                                                                cursor: reordering ? "wait" : "grab",
                                                                opacity: isDragging ? 0.35 : 1,
                                                                backgroundColor: isDragOver ? "#e8f4fd" : undefined,
                                                                borderTop: isDragOver ? "2px solid #0d6efd" : undefined,
                                                                transition: "background-color 0.15s ease"
                                                            }}
                                                        >
                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <FiMove size={15} className="text-muted pointer" title="Drag to reorder" />
                                                                    <Badge color={idx === 0 ? "primary" : "light"} className={idx === 0 ? "text-white" : "text-dark border"} style={{ minWidth: "22px" }}>
                                                                        {idx + 1}
                                                                    </Badge>
                                                                </div>
                                                            </td>
                                                            <td className="fw-bold">
                                                                <div className="text-truncate" style={{ maxWidth: "220px" }} title={variant.title || variant.name}>
                                                                    {variant.title || variant.name || "N/A"}
                                                                </div>
                                                                {variant.sku && <div className="small text-muted font-monospace">{variant.sku}</div>}
                                                            </td>
                                                            <td className="fw-bold text-success">${variant.price || 0}</td>
                                                            <td>
                                                                <Badge color="light" className="text-dark border">
                                                                    {variant.stc_quantity ?? variant.total_quantity ?? variant.quantity ?? variant.qty ?? 0}
                                                                </Badge>
                                                            </td>
                                                            {isVariant && attributeKeys.map((attrKey, kIdx) => {
                                                                const lower = String(attrKey).toLowerCase().trim();
                                                                const val = variant.variantAttributes?.[attrKey] ?? variant.variantAttributes?.[lower] ?? variant.attributes?.[attrKey] ?? variant.attributes?.[lower] ?? "";
                                                                return (
                                                                    <td key={kIdx}>
                                                                        {val ? (
                                                                            <Badge color="light" className="text-primary border px-2 py-1">
                                                                                {val}
                                                                            </Badge>
                                                                        ) : (
                                                                            <span className="text-muted small">—</span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                            <td><Badge color="light" className="text-dark border">{variant.condition || "N/A"}</Badge></td>
                                                            <td>
                                                                <Badge color={variant.status === "publish" ? "success" : "secondary"}>
                                                                    {variant.status || "N/A"}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-end">
                                                                <div className="d-flex align-items-center justify-content-end gap-1">
                                                                    <Button 
                                                                        color="light" 
                                                                        size="sm" 
                                                                        className="p-1 text-muted border-0 shadow-none" 
                                                                        disabled={idx === 0 || reordering}
                                                                        onClick={() => moveVariant(idx, idx - 1)}
                                                                        title={`Move ${itemTypeLabel} Up`}
                                                                    >
                                                                        <FiChevronUp size={16} />
                                                                    </Button>
                                                                    <Button 
                                                                        color="light" 
                                                                        size="sm" 
                                                                        className="p-1 text-muted border-0 shadow-none" 
                                                                        disabled={idx === groupData.variants.length - 1 || reordering}
                                                                        onClick={() => moveVariant(idx, idx + 1)}
                                                                        title={`Move ${itemTypeLabel} Down`}
                                                                    >
                                                                        <FiChevronDown size={16} />
                                                                    </Button>
                                                                    <Button 
                                                                        color="link" 
                                                                        className="text-danger p-1 ms-1" 
                                                                        disabled={reordering}
                                                                        onClick={() => handleRemoveVariant(pId)}
                                                                        title={`Remove ${itemTypeLabel}`}
                                                                    >
                                                                        <FiTrash2 size={16} />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-4 bg-light rounded">
                                        <FiBox size={30} className="mb-2 text-secondary" />
                                        <p className="mb-0">No {itemsPluralLabel.toLowerCase()} found in this group.</p>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={4}>
                        {/* Manufacturer & Category */}
                        <Card className="border-0 shadow-sm mb-4">
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                        <FiLayers size={16} />
                                    </div>
                                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider">Categorization</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <label className="text-muted small mb-1">Manufacturer</label>
                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                            {groupData?.mfgDetails?.logo && (
                                                <img src={groupData.mfgDetails.logo} alt="mfg" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                                            )}
                                            {groupData?.mfg_name || groupData?.mfgDetails?.name || "N/A"}
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <label className="text-muted small mb-1">Category</label>
                                        <div className="fw-bold text-dark">{groupData?.category?.categoryName || "N/A"}</div>
                                    </Col>
                                    <Col md={12}>
                                        <label className="text-muted small mb-1">Sub Category</label>
                                        <div className="fw-bold text-dark">{groupData?.subCategory?.subCategoryName || "N/A"}</div>
                                    </Col>
                                    {isVariant && (
                                        <Col md={12}>
                                            <label className="text-muted small mb-1">Variant Attributes</label>
                                            <div className="d-flex flex-wrap gap-2 mt-1">
                                                {groupData?.variantAttributes?.length > 0 ? (
                                                    groupData.variantAttributes.map((attr, idx) => (
                                                        <Badge color="light" className="text-dark border" key={idx}>{attr}</Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-muted small">None</span>
                                                )}
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </CardBody>
                        </Card>

                        {/* Media & Images */}
                        <Card className="border-0 shadow-sm mb-4">
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                        <FiImage size={16} />
                                    </div>
                                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider">Media</h6>
                                </div>
                                <div className="text-center mb-3">
                                    {groupData?.imageUrl ? (
                                        <img src={groupData.imageUrl} alt="Group" className="img-fluid rounded border" style={{ maxHeight: '200px', objectFit: 'contain' }} />
                                    ) : (
                                        <div className="bg-light p-4 rounded text-muted d-flex flex-column align-items-center justify-content-center" style={{ height: '150px' }}>
                                            <FiImage size={30} className="mb-2" />
                                            <span>No Image</span>
                                        </div>
                                    )}
                                </div>
                                {groupData?.images?.length > 0 && (
                                    <div>
                                        <label className="text-muted small mb-2 d-block">Additional Images</label>
                                        <div className="d-flex gap-2 flex-wrap">
                                            {groupData.images.map((img, idx) => (
                                                <img key={idx} src={img} alt="additional" className="rounded border" style={{ width: '60px', height: '60px', objectFit: 'cover' }} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        {/* Pricing & Stock Metrics */}
                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                                    <div className="bg-primary-soft p-2 rounded-3 text-primary">
                                        <FiActivity size={16} />
                                    </div>
                                    <h6 className="fw-bold text-dark mb-0 text-uppercase tracking-wider">Metrics</h6>
                                </div>
                                <Row className="g-3">
                                    <Col md={6}>
                                        <label className="text-muted small mb-1">Total {itemsPluralLabel}</label>
                                        <div className="fw-bold fs-5 text-dark">{groupData?.totalVariants || groupData?.variants?.length || 0}</div>
                                    </Col>
                                    <Col md={6}>
                                        <label className="text-muted small mb-1">Visible {itemsPluralLabel}</label>
                                        <div className="fw-bold fs-5 text-dark">{groupData?.visibleVariantCount || groupData?.variants?.length || 0}</div>
                                    </Col>
                                    <Col md={6}>
                                        <label className="text-muted small mb-1">Min Price</label>
                                        <div className="fw-bold fs-5 text-dark d-flex align-items-center"><FiDollarSign size={16} className="text-muted" /> {groupData?.minPrice || 0}</div>
                                    </Col>
                                    <Col md={6}>
                                        <label className="text-muted small mb-1">Max Price</label>
                                        <div className="fw-bold fs-5 text-dark d-flex align-items-center"><FiDollarSign size={16} className="text-muted" /> {groupData?.maxPrice || 0}</div>
                                    </Col>
                                    <Col md={12}>
                                        <label className="text-muted small mb-1">Global Stock Status</label>
                                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                            <div className={`rounded-circle ${groupData?.inStock ? 'bg-success' : 'bg-danger'}`} style={{ width: 10, height: 10 }}></div>
                                            {groupData?.inStock ? "In Stock" : "Out of Stock"}
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default ProductGroupDetails;
